import { Editor, Element, Range, Text, Transforms } from 'slate';

// Track changes ("Reviewing" mode) for SlateEditor.
//
// Text leaves carry `ins` / `del` marks with a payload { a: author id, n: author
// name, t: minute-resolution ISO time }. In reviewing mode typing is marked as an
// insertion and deleting marks text as deleted (struck through) instead of
// removing it; deleting your own pending insertion removes it for real. Paragraph
// joins/splits, tables and citation chips are not tracked.

// Clearly different hues, dark enough to read as text on white: red, green, cyan, purple,
// orange, blue, magenta, brown, olive, slate.
export const AUTHOR_PALETTE = [
  '#d32f2f', '#2e7d32', '#00838f', '#7b1fa2', '#ef6c00',
  '#1565c0', '#c2185b', '#6d4c41', '#827717', '#455a64',
];

// Colors are handed out in the order authors are first seen (in the text or among the people
// online), so the first ten authors always get ten different colors. Hashing ids, as before,
// could give two co-authors the same or a neighbouring shade.
const assigned = new Map();

export const authorColor = (authorId = '') => {
  const key = String(authorId);
  if (!assigned.has(key)) assigned.set(key, AUTHOR_PALETTE[assigned.size % AUTHOR_PALETTE.length]);
  return assigned.get(key);
};

const changePayload = (author) => ({ a: String(author.id), n: author.name || 'Unknown', t: new Date().toISOString().slice(0, 16) });

const blockPath = (editor, point) => Editor.above(editor, { at: point, match: (n) => Element.isElement(n) && Editor.isBlock(editor, n) })?.[1];

const sameBlock = (editor, a, b) => {
  const pa = blockPath(editor, a);
  const pb = blockPath(editor, b);
  return pa && pb && pa.join() === pb.join();
};

const containsVoid = (editor, range) =>
  Array.from(Editor.nodes(editor, { at: range, match: (n) => Element.isElement(n) && Editor.isVoid(editor, n) })).length > 0;

/** Mark text in range as deleted by the author; their own pending insertions are removed. */
const markDeleted = (editor, range, author) => {
  const me = String(author.id);
  // Reuse an adjacent deletion's payload so consecutive deletions merge into one leaf
  const existing = Editor.marks(editor);
  const change = existing?.del?.a === me ? existing.del : changePayload(author);
  const rangeRef = Editor.rangeRef(editor, range);
  Editor.withoutNormalizing(editor, () => {
    Transforms.setNodes(editor, { del: change }, { at: range, match: (n) => Text.isText(n) && !n.del, split: true });
    const at = rangeRef.current;
    if (!at) return;
    const own = Array.from(Editor.nodes(editor, {
      at, match: (n) => Text.isText(n) && n.ins?.a === me && n.del?.a === me,
    }));
    own.map(([, path]) => Editor.pathRef(editor, path)).reverse().forEach((ref) => {
      const path = ref.unref();
      if (path) Transforms.removeNodes(editor, { at: path });
    });
  });
  rangeRef.unref();
};

/**
 * Plugin. ctxRef.current = { reviewing: boolean, author: { id, name } } is read on
 * every operation so the mode can change without recreating the editor.
 */
export const withTrackChanges = (editor, ctxRef) => {
  const { insertText, deleteBackward, deleteForward, deleteFragment, insertData } = editor;
  const active = () => Boolean(ctxRef.current?.reviewing && ctxRef.current?.author);

  editor.insertText = (text) => {
    if (!active()) {
      // Editing mode: typing next to a tracked change must not inherit its ins/del mark
      // (Slate copies the marks of the text before the cursor)
      const marks = Editor.marks(editor);
      if (marks?.ins || marks?.del) {
        const { ins, del, ...rest } = marks; // eslint-disable-line no-unused-vars
        editor.marks = rest;
      }
      insertText(text);
      return;
    }
    const { author } = ctxRef.current;
    if (editor.selection && Range.isExpanded(editor.selection)) {
      // Typing over a selection: strike the old text, insert after it
      const endRef = Editor.pointRef(editor, Range.end(editor.selection));
      markDeleted(editor, editor.selection, author);
      Transforms.select(editor, endRef.unref());
    }
    const marks = { ...(Editor.marks(editor) || {}) };
    delete marks.del;
    const me = String(author.id);
    editor.marks = { ...marks, ins: marks.ins?.a === me ? marks.ins : changePayload(author) };
    insertText(text);
  };

  const trackedDelete = (unit, backward) => {
    const { selection } = editor;
    if (!active() || !selection || !Range.isCollapsed(selection)) return false;
    const origin = selection.anchor;
    const target = backward ? Editor.before(editor, origin, { unit }) : Editor.after(editor, origin, { unit });
    if (!target) return true;
    const range = backward ? { anchor: target, focus: origin } : { anchor: origin, focus: target };
    // Merging paragraphs and removing citation chips are applied directly
    if (!sameBlock(editor, origin, target) || containsVoid(editor, range)) return false;
    // Backspace lands before the struck text, Delete after it — both are `target`
    const landing = Editor.pointRef(editor, target);
    markDeleted(editor, range, ctxRef.current.author);
    Transforms.select(editor, landing.unref());
    return true;
  };

  editor.deleteBackward = (unit) => { if (!trackedDelete(unit, true)) deleteBackward(unit); };
  editor.deleteForward = (unit) => { if (!trackedDelete(unit, false)) deleteForward(unit); };

  editor.deleteFragment = (options) => {
    const { selection } = editor;
    if (!active() || !selection || Range.isCollapsed(selection)) { deleteFragment(options); return; }
    const backward = options?.direction !== 'forward';
    const landing = Editor.pointRef(editor, backward ? Range.start(selection) : Range.end(selection));
    markDeleted(editor, selection, ctxRef.current.author);
    Transforms.select(editor, landing.unref());
  };

  // Pasting while reviewing inserts plain text so it is tracked like typing
  editor.insertData = (data) => {
    const text = data.getData('text/plain');
    if (!active() || !text) { insertData(data); return; }
    text.split(/\r?\n/).forEach((line, i) => {
      if (i > 0) editor.insertBreak();
      if (line) editor.insertText(line);
    });
  };

  return editor;
};

/** True if the document contains any tracked change. */
export const hasChanges = (editor) =>
  Array.from(Editor.nodes(editor, { at: [], match: (n) => Text.isText(n) && (n.ins || n.del) })).length > 0;

/**
 * Accept or reject tracked changes in `range` (default: the change at the cursor,
 * or the selection; pass `all` for the whole document).
 * Accept: insertions become normal text, deletions disappear.
 * Reject: insertions disappear, deletions are restored.
 */
export const resolveChanges = (editor, accept, all = false) => {
  let at;
  if (all || !editor.selection) at = [];
  else if (Range.isCollapsed(editor.selection)) at = Editor.leaf(editor, editor.selection.anchor)[1];
  else at = editor.selection;

  Editor.withoutNormalizing(editor, () => {
    const entries = Array.from(Editor.nodes(editor, { at, match: (n) => Text.isText(n) && (n.ins || n.del) }));
    entries
      .map(([node, path]) => [node, Editor.pathRef(editor, path)])
      .reverse()
      .forEach(([node, ref]) => {
        const path = ref.unref();
        if (!path) return;
        const remove = accept ? Boolean(node.del) : Boolean(node.ins);
        if (remove) Transforms.removeNodes(editor, { at: path });
        else Transforms.unsetNodes(editor, accept ? ['ins'] : ['del'], { at: path });
      });
  });
};
